<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Create a new user</h1>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="!userCreated" class="container">
            <div class="form-group">
                <label for="Email">Email:</label>
                <input v-model="email" type="email" class="form-control" placeholder="Work email of an user">
                <ValidationError context="Email" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="FirstName">First name:</label>
                <input v-model="firstName" type="text" class="form-control" placeholder="First name of an user">
                <ValidationError context="FirstName" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group">
                <label for="LastName">Last name:</label>
                <input v-model="lastName" type="text" class="form-control" placeholder="Last name of an user">
                <ValidationError context="LastName" :errors="validationErrors"></ValidationError>
            </div>

            <div class="form-group form-check">
                <input v-model="setInitialPassword" type="checkbox" class="form-check-input" id="setInitialPassword">
                <label class="form-check-label" for="setInitialPassword">Set initial password manually</label>
            </div>

            <div v-show="setInitialPassword">
                <div class="form-group">
                    <label for="Password">Initial password:</label>
                    <input v-model="password" type="password" class="form-control">
                    <ValidationError context="Password" :errors="validationErrors"></ValidationError>
                </div>

                <div class="form-group" v-show="password">
                    <label for="PasswordConfirmation">Initial password confirmation:</label>
                    <input v-model="passwordConfirmation" type="password" class="form-control" :class="{ 'is-invalid': passwordConfirmationInvalid }">
                    <div class="invalid-feedback">Initial password confirmation doesn't match</div>
                </div>
            </div>

            <button v-if="!invokingCreateUserCommandV1" @click="handleCreateUserCommandV1" type="button" class="btn btn-primary btn-lg" :disabled="passwordConfirmationInvalid">Create</button>
            <button v-else type="button" class="btn btn-primary btn-lg" disabled>Creating...</button>
        </div>

        <div v-else class="container">
            <h4 class="fg-color-blue">User was created and initial system generated password was sent on {{email}}</h4>
            <br />

            <a :href="`/#/admin/organizations/users/manage/type/${email}`" class="btn btn-success text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-external-link" aria-hidden="true"></i>
                <span>Manage user roles</span>
            </a>

            <h4 class="fg-color-blue" style="margin: 20px 0;">or</h4>

            <a :href="`/#/admin/organizations/users/manage/workspaces/${email}`" class="btn btn-secondary text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-external-link" aria-hidden="true"></i>
                <span>Manage user workspaces</span>
            </a>

            <h4 class="fg-color-blue" style="margin: 20px 0;">or</h4>

            <button @click="handleFinish" class="btn btn-secondary text-white" style="padding: 0.7em 2em;">
                <i class="fa fa-user-plus" aria-hidden="true"></i>
                <span>Add another user</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute } from 'vue-router'
    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'
    import ValidationError from '@common/Components/ValidationError.vue'
    import { useCurrentUserQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import { useCreateUserCommandV1 } from '@Organizations/Users/commands-vue-composables'

    // ---------------- route ----------------

    const route = useRoute()
    const tid = computed(() => Number(route.params.tid))

    // ---------------- local state ----------------

    const email = ref('')
    const firstName = ref('')
    const lastName = ref('')
    const password = ref('')
    const passwordConfirmation = ref('')
    const setInitialPassword = ref(false)

    // ---------------- validation ----------------

    const passwordConfirmationInvalid = computed(() =>
    {
        if (!setInitialPassword.value) return false
        return !!passwordConfirmation.value && passwordConfirmation.value !== password.value
    })

    const { runCurrentUserQueryV1 } =
        useCurrentUserQueryV1(false, false, true)

    // ---------------- command ----------------

    const {
        invokeCreateUserCommandV1,
        invokingCreateUserCommandV1,
        validationErrors,
        succeedCreateUserCommandV1,
        errorCreateUserCommandV1
    } = useCreateUserCommandV1(
        '0',
        tid,
        email,
        firstName,
        lastName,
        false,
        false,
        false,
        true,
        setInitialPassword,
        password
    )

    const userCreated = computed(() => succeedCreateUserCommandV1.value === true)

    onMounted(() =>
    {
        document.title = 'Create a new user'
        runCurrentUserQueryV1()
    })

    // ---------------- handlers ----------------

    function handleCreateUserCommandV1()
    {
        if (passwordConfirmationInvalid.value) return
        invokeCreateUserCommandV1()
    }

    function handleFinish()
    {
        email.value = ''
        firstName.value = ''
        lastName.value = ''
        password.value = ''
        passwordConfirmation.value = ''
        setInitialPassword.value = false
    }

    // ---------------- error handling ----------------

    watch(errorCreateUserCommandV1, (err) =>
    {
        if (err)
        {
            alert(err)
        }
    })
</script>

